<template>
  <div class="contract-list">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2>合同管理</h2>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-card shadow="hover">
        <div class="stat-item">
          <div class="stat-value">{{ stats.total }}</div>
          <div class="stat-label">合同总数</div>
        </div>
      </el-card>
      <el-card shadow="hover">
        <div class="stat-item">
          <div class="stat-value warning">{{ stats.pendingSign }}</div>
          <div class="stat-label">待签署</div>
        </div>
      </el-card>
      <el-card shadow="hover">
        <div class="stat-item">
          <div class="stat-value success">{{ stats.signed }}</div>
          <div class="stat-label">已签署</div>
        </div>
      </el-card>
      <el-card shadow="hover">
        <div class="stat-item">
          <div class="stat-value primary">{{ stats.completed }}</div>
          <div class="stat-label">已完成</div>
        </div>
      </el-card>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input v-model="searchParams.keyword" placeholder="搜索合同标题/编号" clearable style="width: 200px" @keyup.enter="loadData" />
      <el-select v-model="searchParams.status" placeholder="状态" clearable style="width: 150px">
        <el-option label="草稿" value="draft" />
        <el-option label="待签署" value="pending" />
        <el-option label="部分签署" value="partial" />
        <el-option label="已签署" value="signed" />
        <el-option label="待打印" value="print_pending" />
        <el-option label="已打印" value="printed" />
        <el-option label="已邮寄" value="mailed" />
        <el-option label="已完成" value="completed" />
        <el-option label="已取消" value="cancelled" />
      </el-select>
      <el-select v-model="searchParams.businessType" placeholder="业务类型" clearable style="width: 150px">
        <el-option label="债务确认" value="debt" />
        <el-option label="商家结算" value="merchant_settlement" />
        <el-option label="亲友帮还" value="friend_help" />
      </el-select>
      <el-button type="primary" @click="loadData">搜索</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <!-- 数据表格 -->
    <el-table :data="tableData" v-loading="loading" stripe>
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="contractNo" label="合同编号" width="180" />
      <el-table-column prop="title" label="合同标题" min-width="200" />
      <el-table-column prop="businessType" label="业务类型" width="120">
        <template #default="{ row }">
          <el-tag>{{ businessTypeMap[row.businessType] || row.businessType }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="signMethod" label="签名方式" width="120">
        <template #default="{ row }">
          <el-tag :type="row.signMethod === 'self' ? 'success' : 'primary'">
            {{ signMethodMap[row.signMethod] || row.signMethod }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="getStatusType(row.status)">{{ statusMap[row.status] || row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="180">
        <template #default="{ row }">
          {{ formatDate(row.createdAt) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="250" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="handleView(row)">查看</el-button>
          <template v-if="row.status === 'signed'">
            <el-button link type="primary" @click="handlePrint(row)">打印</el-button>
          </template>
          <template v-if="row.status === 'printed'">
            <el-button link type="primary" @click="handleMail(row)">邮寄</el-button>
          </template>
          <template v-if="row.status === 'mailed'">
            <el-button link type="success" @click="handleComplete(row)">完成</el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.limit"
        :total="pagination.total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next"
        @size-change="loadData"
        @current-change="loadData"
      />
    </div>

    <!-- 详情对话框 -->
    <el-dialog v-model="detailVisible" title="合同详情" width="900px">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="合同编号">{{ currentContract.contractNo }}</el-descriptions-item>
        <el-descriptions-item label="合同标题">{{ currentContract.title }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="getStatusType(currentContract.status)">{{ statusMap[currentContract.status] }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="签名方式">{{ signMethodMap[currentContract.signMethod] }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDate(currentContract.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="签署时间">{{ formatDate(currentContract.signedAt) }}</el-descriptions-item>
        <el-descriptions-item label="物流单号" v-if="currentContract.trackingNo">{{ currentContract.trackingNo }}</el-descriptions-item>
        <el-descriptions-item label="快递公司" v-if="currentContract.expressCompany">{{ currentContract.expressCompany }}</el-descriptions-item>
      </el-descriptions>
      
      <div class="contract-content" v-if="currentContract.content">
        <h4>合同内容</h4>
        <div class="content-preview" v-html="currentContract.content"></div>
      </div>

      <div class="signers-info" v-if="currentContract.signers">
        <h4>签署方</h4>
        <el-table :data="parseSigners(currentContract.signers)" size="small">
          <el-table-column prop="name" label="姓名" />
          <el-table-column prop="role" label="角色" />
          <el-table-column prop="signStatus" label="签署状态">
            <template #default="{ row }">
              <el-tag :type="row.signStatus === 'signed' ? 'success' : 'warning'">
                {{ row.signStatus === 'signed' ? '已签署' : '待签署' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="signTime" label="签署时间">
            <template #default="{ row }">
              {{ row.signTime ? formatDate(row.signTime) : '-' }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <!-- 邮寄对话框 -->
    <el-dialog v-model="mailDialogVisible" title="填写物流信息" width="500px">
      <el-form :model="mailForm" label-width="100px">
        <el-form-item label="快递公司">
          <el-select v-model="mailForm.expressCompany" style="width: 100%">
            <el-option label="顺丰速运" value="顺丰速运" />
            <el-option label="中通快递" value="中通快递" />
            <el-option label="圆通快递" value="圆通快递" />
            <el-option label="韵达快递" value="韵达快递" />
            <el-option label="申通快递" value="申通快递" />
            <el-option label="EMS" value="EMS" />
          </el-select>
        </el-form-item>
        <el-form-item label="物流单号">
          <el-input v-model="mailForm.trackingNo" placeholder="请输入物流单号" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="mailDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitMail">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import contractApi from '@/api/modules/contract'

// 映射表
const statusMap = {
  draft: '草稿',
  pending: '待签署',
  partial: '部分签署',
  signed: '已签署',
  print_pending: '待打印',
  printed: '已打印',
  mailed: '已邮寄',
  completed: '已完成',
  cancelled: '已取消',
  expired: '已过期'
}

const signMethodMap = {
  self: '富福德正网签',
  tencent: '腾讯电子签',
  fadada: '法大大',
  esign: 'e签宝'
}

const businessTypeMap = {
  debt: '债务确认',
  merchant_settlement: '商家结算',
  friend_help: '亲友帮还'
}

// 状态
const loading = ref(false)
const detailVisible = ref(false)
const mailDialogVisible = ref(false)
const tableData = ref([])
const currentContract = ref({})
const mailForm = reactive({
  id: null,
  expressCompany: '',
  trackingNo: ''
})

// 统计数据
const stats = reactive({
  total: 0,
  pendingSign: 0,
  signed: 0,
  completed: 0
})

// 分页
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 搜索参数
const searchParams = reactive({
  keyword: '',
  status: '',
  businessType: ''
})

// 获取状态类型
const getStatusType = (status) => {
  const map = {
    draft: 'info',
    pending: 'warning',
    partial: 'warning',
    signed: 'success',
    print_pending: 'warning',
    printed: 'primary',
    mailed: 'primary',
    completed: 'success',
    cancelled: 'danger',
    expired: 'danger'
  }
  return map[status] || 'info'
}

// 解析签署方
const parseSigners = (signersStr) => {
  try {
    return JSON.parse(signersStr) || []
  } catch {
    return []
  }
}

// 加载统计
const loadStats = async () => {
  try {
    const res = await contractApi.getContractStats()
    Object.assign(stats, res.data)
  } catch (error) {
    console.error('加载统计失败')
  }
}

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    const res = await contractApi.getContractList({
      page: pagination.page,
      limit: pagination.limit,
      ...searchParams
    })
    tableData.value = res.list || []
    pagination.total = res.total || 0
  } catch (error) {
    ElMessage.error('加载数据失败')
  } finally {
    loading.value = false
  }
}

// 重置搜索
const resetSearch = () => {
  searchParams.keyword = ''
  searchParams.status = ''
  searchParams.businessType = ''
  loadData()
}

// 查看详情
const handleView = async (row) => {
  try {
    const res = await contractApi.getContract(row.id)
    currentContract.value = res.data
    detailVisible.value = true
  } catch (error) {
    ElMessage.error('获取详情失败')
  }
}

// 打印
const handlePrint = async (row) => {
  try {
    await contractApi.updateContractStatus(row.id, { status: 'printed' })
    ElMessage.success('已标记为打印')
    loadData()
    loadStats()
  } catch (error) {
    ElMessage.error('操作失败')
  }
}

// 邮寄
const handleMail = (row) => {
  mailForm.id = row.id
  mailForm.expressCompany = ''
  mailForm.trackingNo = ''
  mailDialogVisible.value = true
}

// 提交邮寄
const submitMail = async () => {
  if (!mailForm.expressCompany || !mailForm.trackingNo) {
    ElMessage.warning('请填写完整物流信息')
    return
  }
  try {
    await contractApi.updateContractStatus(mailForm.id, {
      status: 'mailed',
      expressCompany: mailForm.expressCompany,
      trackingNo: mailForm.trackingNo
    })
    ElMessage.success('邮寄信息已保存')
    mailDialogVisible.value = false
    loadData()
    loadStats()
  } catch (error) {
    ElMessage.error('操作失败')
  }
}

// 完成
const handleComplete = async (row) => {
  try {
    await ElMessageBox.confirm('确认纸质合同已回收归档？', '提示', { type: 'warning' })
    await contractApi.updateContractStatus(row.id, { status: 'completed' })
    ElMessage.success('合同已完成')
    loadData()
    loadStats()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('操作失败')
    }
  }
}

// 格式化日期
const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleString('zh-CN')
}

onMounted(() => {
  loadData()
  loadStats()
})
</script>

<style scoped>
.contract-list {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 18px;
  color: #303133;
}

.stats-cards {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.stats-cards .el-card {
  flex: 1;
}

.stat-item {
  text-align: center;
  padding: 10px 0;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #409eff;
}

.stat-value.warning { color: #e6a23c; }
.stat-value.success { color: #67c23a; }
.stat-value.primary { color: #409eff; }

.stat-label {
  color: #909399;
  margin-top: 5px;
}

.search-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.contract-content, .signers-info {
  margin-top: 20px;
}

.contract-content h4, .signers-info h4 {
  margin-bottom: 10px;
  color: #303133;
}

.content-preview {
  padding: 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fafafa;
  max-height: 300px;
  overflow-y: auto;
}
</style>
